// Library Imports
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
  TableHeaderColumn,
  TableSortableHeaderColumn,
} from '@unite-us/app-components';
import { useFeatureFlag } from 'common/hooks';

// Util Imports
import { tableWidth } from 'common/helpers/Tables';
import { PROGRAM_COLUMN_WIDTH } from '../../utils/tableLayout';

// Component Imports
import EmptyCase from './EmptyCase';
import CaseTableRow from './CaseTableRow';
import '../../stylesheets/facesheetTable.scss';

const CasesTable = (props) => {
  const {
    cases,
    groupId,
    isCaseManager,
    condensedView = false,
    sortBy,
    sortDirection,
    onSortChange,
    standardizedTable = false,
  } = props;

  if (_.isEmpty(cases)) {
    return <EmptyCase />;
  }

  const setSort = ({ sortBy: sort, sortDirection: direction }) => {
    onSortChange(sort, direction);
  };

  const displayCases = condensedView ? cases.slice(0, 2) : cases;
  const crtb2182ClarifyReferralLanguage = useFeatureFlag('crtb-2182-clarify-referral-language');
  const showProgramNameReferralsCasesDashboard =
    useFeatureFlag('crtb-2646-show-program-name-referrals-cases-dashboard');

  const dashboardHeadersWithSortingEnabled = (
    <TableHeader>
      <TableRow>
        {crtb2182ClarifyReferralLanguage && <TableHeaderColumn style={{ width: tableWidth('icon') }} />}
        <TableHeaderColumn style={{ width: tableWidth('small') }}>
          Service Type
        </TableHeaderColumn>
        {showProgramNameReferralsCasesDashboard && (
          <TableSortableHeaderColumn
            style={{ width: standardizedTable ? PROGRAM_COLUMN_WIDTH : tableWidth('large') }}
            colName={'program.name'}
            sortBy={sortBy}
            sortDirection={sortDirection}
            setSort={setSort}
          >
            Program
          </TableSortableHeaderColumn>
        )}
        <TableSortableHeaderColumn
          style={{ width: tableWidth('small') }}
          colName={'opened_date'}
          sortBy={sortBy}
          sortDirection={sortDirection}
          setSort={setSort}
        >
          Date Opened
        </TableSortableHeaderColumn>
        <TableHeaderColumn style={{ width: tableWidth('small') }}>
          Status
        </TableHeaderColumn>
        <TableHeaderColumn style={{ width: tableWidth('medium') }}>
          Managing Organization
        </TableHeaderColumn>
        <TableSortableHeaderColumn
          style={{ width: tableWidth('small') }}
          colName={'updated_at'}
          sortBy={sortBy}
          sortDirection={sortDirection}
          setSort={setSort}
        >
          Last Updated
        </TableSortableHeaderColumn>
      </TableRow>
    </TableHeader>
  );
  return (
    <Table
      noBorder
      id="casesTable"
      className={standardizedTable ? 'facesheet-table' : ''}
    >
      {dashboardHeadersWithSortingEnabled}
      <TableBody>
        {displayCases.map((serviceCase, index) => {
          const serviceType = serviceCase.service;
          const primaryWorker = serviceCase.primary_worker;
          const serviceCaseGroup = serviceCase.provider;
          const isPendingAuth = isCaseManager && serviceCase?.state === 'pending_authorization';

          return (
            <CaseTableRow
              serviceCase={serviceCase}
              serviceType={serviceType}
              primaryWorker={primaryWorker}
              serviceCaseGroup={serviceCaseGroup}
              groupId={groupId}
              isCaseManager={isCaseManager}
              index={index}
              key={index}
              isPendingAuth={isPendingAuth}
              standardizedTable={standardizedTable}
            />
          );
        })}
      </TableBody>
    </Table>
  );
};

CasesTable.propTypes = {
  cases: PropTypes.array,
  condensedView: PropTypes.bool.isRequired,
  groupId: PropTypes.string.isRequired,
  isCaseManager: PropTypes.bool.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortDirection: PropTypes.string.isRequired,
  onSortChange: PropTypes.func,
  standardizedTable: PropTypes.bool,
};

export default CasesTable;
